import {
	acceptCompletion,
	autocompletion,
	closeBrackets,
	type Completion,
	type CompletionContext,
	completionKeymap,
	startCompletion
} from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentLess, indentMore, toggleLineComment } from "@codemirror/commands";
import {
	bracketMatching,
	defaultHighlightStyle,
	HighlightStyle,
	indentUnit,
	StreamLanguage,
	syntaxHighlighting
} from "@codemirror/language";
import { type Diagnostic, forceLinting, linter, lintGutter } from "@codemirror/lint";
import { Compartment, EditorState, Prec } from "@codemirror/state";
import { placeholder as cmPlaceholder, drawSelection, EditorView, highlightActiveLine, keymap } from "@codemirror/view";
import { tags } from "@lezer/highlight";

import { FORMULA_FUNCTION_REGEX, FORMULA_IDENTIFIER_REGEX_SOURCE } from "./formulaEditor.constants";
import { FormulaCompletionOption, FormulaDiagnostic, FormulaFormatMode, SuggestionKind } from "./formulaEditor.types";

export interface FormulaCodeMirrorEditorOptions {
	container: HTMLElement;
	placeholder?: string;
	readOnly: boolean;
	validate: (value: string) => FormulaDiagnostic[];
	value: string;
	onChange: (value: string) => void;
	provideCompletionItems: (
		value: string,
		offset: number,
		explicit: boolean
	) => Promise<FormulaCompletionOption[]>;
	format?: (value: string, mode?: FormulaFormatMode) => string;
}

export interface FormulaCodeMirrorEditor {
	destroy(): void;
	focus(): void;
	format(mode?: FormulaFormatMode): void;
	getContentHeight(): number;
	getLastUsedFormatMode(): FormulaFormatMode;
	getValue(): string;
	setReadOnly(readOnly: boolean): void;
	setValue(value: string): void;
	triggerCompletion(): void;
	validate(): void;
}

const FUNCTION_PATTERN = new RegExp(`^${FORMULA_FUNCTION_REGEX.source}`, FORMULA_FUNCTION_REGEX.flags);
const IDENTIFIER_PATTERN = new RegExp(`^${FORMULA_IDENTIFIER_REGEX_SOURCE}`, "u");
const FORMULA_INDENT_UNIT = "  ";

function isToggleCommentShortcutEvent(event: KeyboardEvent): boolean {
	return event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey && event.key === ":";
}

const formulaLanguage = StreamLanguage.define({
	name: "formula",
	token(stream) {
		if (stream.eatSpace()) {
			return null;
		}

		if (stream.match(/^\/\/.*$/u)) {
			return "lineComment";
		}

		if (stream.match(/^"(?:[^"]|"")*"?/u)) {
			return "string";
		}

		if (stream.match(/^(?:>=|<=|==|!=|>|<)/u)) {
			return "operator";
		}

		if (stream.match(/^[+\-*\/]/u)) {
			return "operator";
		}

		if (stream.match(/^[()]/u)) {
			return "paren";
		}

		if (stream.match(/^(?:AND|OR)\b/u)) {
			return "keyword";
		}

		if (stream.match(/^[0-9]+[.,][0-9]+/u)) {
			return "number";
		}

		if (stream.match(/^[0-9]+/u)) {
			return "number";
		}

		if (stream.match(FUNCTION_PATTERN)) {
			return "functionName";
		}

		if (stream.match(IDENTIFIER_PATTERN)) {
			return "variableName";
		}

		stream.next();
		return null;
	},
	languageData: {
		commentTokens: { line: "//" }
	},
	tokenTable: {
		functionName: tags.function(tags.variableName),
		lineComment: tags.lineComment,
		number: tags.number,
		operator: tags.operator,
		paren: tags.paren,
		string: tags.string,
		variableName: tags.variableName
	}
});

const formulaHighlightStyle = HighlightStyle.define([
	{ tag: tags.lineComment, color: "#1f7a1f" },
	{ tag: tags.keyword, color: "#9c4d14", fontWeight: "600" },
	{ tag: tags.function(tags.variableName), color: "#17636e", fontWeight: "600" },
	{ tag: tags.variableName, color: "#1f2937" },
	{ tag: tags.number, color: "#7c2d12" },
	{ tag: tags.string, color: "#1d4ed8" },
	{ tag: tags.operator, color: "#475569" },
	{ tag: tags.paren, color: "#475569" }
]);

const formulaEditorTheme = EditorView.theme({
	"&": {
		backgroundColor: "#ffffff",
		height: "100%"
	},
	"&.cm-focused": {
		outline: "none"
	},
	".cm-content": {
		caretColor: "#111827",
		minHeight: "100%",
		padding: "8px 0"
	},
	".cm-cursor": {
		borderLeftColor: "#111827"
	},
	".cm-line": {
		padding: "0 12px"
	},
	".cm-scroller": {
		fontFamily: "Menlo, Monaco, 'Courier New', monospace",
		overflow: "auto"
	},
	".cm-selectionBackground, ::selection": {
		backgroundColor: "rgba(59, 130, 246, 0.18)"
	},
	".cm-tooltip.cm-tooltip-autocomplete": {
		border: "1px solid #dbe4f0",
		borderRadius: "8px",
		boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)"
	}
});

const COMPLETION_SECTION_RANKS: Record<string, number> = {
	"Fonctions": 0,
	"Préfixes de variable": 1,
	"Variables": 2,
	"Colonnes de ligne": 3,
	"Tables externes": 4,
	"Valeurs de fonction": 5,
	"Argument attendu": 6,
	"Valeurs attendues": 7
};

function toCodeMirrorCompletionType(kind: SuggestionKind): string {
	const completionTypes: Record<SuggestionKind, string> = {
		field: "property",
		function: "function",
		keyword: "keyword",
		value: "text",
		variable: "variable"
	};

	return completionTypes[kind];
}

function toCodeMirrorCompletionSection(section?: string) {
	if (!section) {
		return undefined;
	}

	return {
		name: section,
		rank: COMPLETION_SECTION_RANKS[section] ?? 99
	};
}

function getOffset(doc: EditorState["doc"], lineNumber: number, column: number): number {
	const line = doc.line(Math.max(1, Math.min(lineNumber, doc.lines)));
	const offset = line.from + Math.max(0, column - 1);
	return Math.max(line.from, Math.min(offset, line.to));
}

function toCodeMirrorDiagnostic(doc: EditorState["doc"], diagnostic: FormulaDiagnostic): Diagnostic {
	const from = getOffset(doc, diagnostic.startLineNumber, diagnostic.startColumn);
	const to = Math.max(from, getOffset(doc, diagnostic.endLineNumber, diagnostic.endColumn));

	return {
		from,
		message: diagnostic.message,
		severity: diagnostic.severity,
		to
	};
}

function applyCompletion(view: EditorView, option: FormulaCompletionOption): void {
	const from = getOffset(view.state.doc, option.range.startLineNumber, option.range.startColumn);
	const to = Math.max(from, getOffset(view.state.doc, option.range.endLineNumber, option.range.endColumn));
	const anchor = from + option.insertText.length;

	view.dispatch({
		changes: { from, insert: option.insertText, to },
		scrollIntoView: true,
		selection: { anchor },
		userEvent: "input.complete"
	});
	view.focus();
}

function createCompletionSource(options: FormulaCodeMirrorEditorOptions) {
	return async (context: CompletionContext) => {
		const completionOptions = await options.provideCompletionItems(
			context.state.doc.toString(),
			context.pos,
			context.explicit
		);

		if (context.aborted || !completionOptions.length) {
			return null;
		}

		const cmOptions: Completion[] = completionOptions.map(option => ({
			apply: view => applyCompletion(view, option),
			detail: option.detail,
			label: option.label,
			section: toCodeMirrorCompletionSection(option.section),
			sortText: option.sortText,
			type: toCodeMirrorCompletionType(option.kind)
		}));

		return {
			filter: false,
			from: context.pos,
			options: cmOptions
		};
	};
}

function applyFormat(
	view: EditorView,
	format: ((value: string, mode?: FormulaFormatMode) => string) | undefined,
	mode: FormulaFormatMode
): boolean {
	if (!format || view.state.readOnly) {
		return false;
	}

	const currentValue = view.state.doc.toString();
	const formattedValue = format(currentValue, mode);

	if (formattedValue === currentValue) {
		return true;
	}

	const anchor = Math.min(view.state.selection.main.head, formattedValue.length);
	view.dispatch({
		changes: { from: 0, insert: formattedValue, to: view.state.doc.length },
		selection: { anchor },
		userEvent: "input"
	});

	return true;
}

function isFormatShortcutEvent(event: KeyboardEvent): boolean {
	const normalizedKey = (event.key || "").toLowerCase();

	return (
		event.altKey &&
		event.shiftKey &&
		!event.ctrlKey &&
		!event.metaKey &&
		(event.code === "KeyF" || normalizedKey === "f")
	);
}

export function createFormulaCodeMirrorEditor(options: FormulaCodeMirrorEditorOptions): FormulaCodeMirrorEditor {
	const readOnlyCompartment = new Compartment();
	const editableCompartment = new Compartment();

	let lastUsedFormatMode: FormulaFormatMode = "compact";

	const runFormat = (view: EditorView, mode?: FormulaFormatMode): boolean => {
		const effectiveMode = mode || lastUsedFormatMode;
		const formatted = applyFormat(view, options.format, effectiveMode);
		if (formatted) {
			lastUsedFormatMode = effectiveMode;
		}

		return formatted;
	};

	const defaultFormatCommand = (view: EditorView) => runFormat(view);
	const runToggleCommentShortcut = (view: EditorView, event: KeyboardEvent): boolean => {
		if (!isToggleCommentShortcutEvent(event)) {
			return false;
		}

		event.preventDefault();
		event.stopPropagation();
		return toggleLineComment(view);
	};
	const runFormatShortcut = (view: EditorView, event: KeyboardEvent): boolean => {
		if (!isFormatShortcutEvent(event)) {
			return false;
		}

		event.preventDefault();
		event.stopPropagation();
		return defaultFormatCommand(view);
	};
	let isApplyingExternalValue = false;

	const view = new EditorView({
		parent: options.container,
		state: EditorState.create({
			doc: options.value,
			extensions: [
				formulaLanguage,
				formulaEditorTheme,
				syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
				syntaxHighlighting(formulaHighlightStyle),
				indentUnit.of(FORMULA_INDENT_UNIT),
				EditorView.lineWrapping,
				history(),
				drawSelection(),
				highlightActiveLine(),
				bracketMatching({ brackets: "()" }),
				closeBrackets(),
				linter(
					viewState => {
						return options
							.validate(viewState.state.doc.toString())
							.map(diagnostic => toCodeMirrorDiagnostic(viewState.state.doc, diagnostic));
					},
					{ delay: 150 }
				),
				lintGutter(),
				autocompletion({
					activateOnTypingDelay: 150,
					override: [createCompletionSource(options)]
				}),
				EditorView.domEventHandlers({
					keydown: (event, viewState) => {
						return runToggleCommentShortcut(viewState, event) || runFormatShortcut(viewState, event);
					}
				}),
				Prec.high(
					keymap.of([
						{
							key: "Mod-Space",
							preventDefault: true,
							run: startCompletion
						},
						{
							key: "Mod-/",
							preventDefault: true,
							run: toggleLineComment
						},
						{
							key: "Tab",
							preventDefault: true,
							run: view => acceptCompletion(view) || indentMore(view)
						},
						{
							key: "Shift-Tab",
							preventDefault: true,
							run: indentLess
						}
					])
				),
				keymap.of([...defaultKeymap, ...historyKeymap, ...completionKeymap]),
				EditorView.updateListener.of(update => {
					if (!update.docChanged || isApplyingExternalValue) {
						return;
					}

					options.onChange(update.state.doc.toString());
				}),
				readOnlyCompartment.of(EditorState.readOnly.of(options.readOnly)),
				editableCompartment.of(EditorView.editable.of(!options.readOnly)),
				options.placeholder ? cmPlaceholder(options.placeholder) : []
			]
		})
	});

	return {
		destroy() {
			view.destroy();
		},
		focus() {
			view.focus();
		},
		format(mode?: FormulaFormatMode) {
			runFormat(view, mode);
		},
		getContentHeight() {
			return Math.ceil(view.contentHeight);
		},
		getLastUsedFormatMode() {
			return lastUsedFormatMode;
		},
		getValue() {
			return view.state.doc.toString();
		},
		setReadOnly(readOnly: boolean) {
			view.dispatch({
				effects: [
					readOnlyCompartment.reconfigure(EditorState.readOnly.of(readOnly)),
					editableCompartment.reconfigure(EditorView.editable.of(!readOnly))
				]
			});
		},
		setValue(value: string) {
			if (value === view.state.doc.toString()) {
				return;
			}

			isApplyingExternalValue = true;
			view.dispatch({
				changes: { from: 0, insert: value, to: view.state.doc.length }
			});
			isApplyingExternalValue = false;
		},
		triggerCompletion() {
			startCompletion(view);
		},
		validate() {
			forceLinting(view);
		}
	};
}
